import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  getMeetingRequestByToken,
  acceptMeetingByToken,
  declineMeetingByToken,
  rsvpBadgeClass,
  rsvpLabel,
} from '../../Api/ScheduleApi';
import type { MeetingRequestDetails } from '../../Api/ScheduleApi';
import Loader from '../Common/Loader';
import { toast } from 'react-toastify';
import { trackEvent } from '../../Utils/analytics';
import { formatInstantRange } from '../../Utils/timeZone';

export default function MeetingAcceptPage() {
  const { token } = useParams<{ token: string }>();
  const [request, setRequest] = useState<MeetingRequestDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState<'accept' | 'decline' | null>(null);

  const reload = async () => {
    if (!token) return;
    const data = await getMeetingRequestByToken(token);
    setRequest(data);
  };

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    getMeetingRequestByToken(token)
      .then((data) => {
        setRequest(data);
        trackEvent('meeting_invite_viewed', {
          request_id: data.id,
          status: data.status,
          entry: 'email_link',
        });
      })
      .catch(() => {
        toast.error('Meeting request not found or link may have expired.');
        setRequest(null);
      })
      .finally(() => setLoading(false));
  }, [token]);

  const handleAccept = async () => {
    if (!token || !request) return;
    setSubmitting(true);
    try {
      await acceptMeetingByToken(token);
      trackEvent('meeting_request_accepted', {
        request_id: request.id,
        entry: 'email_link',
      });
      toast.success('Meeting accepted!');
      setDone('accept');
      await reload();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Unable to accept the meeting.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecline = async () => {
    if (!token || !request) return;
    if (!window.confirm('Decline this meeting invite?')) return;
    setSubmitting(true);
    try {
      await declineMeetingByToken(token);
      trackEvent('meeting_request_declined', {
        request_id: request.id,
        entry: 'email_link',
      });
      toast.success('Meeting invite declined.');
      setDone('decline');
      await reload();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Unable to decline the meeting.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Loader />;
  if (!request) {
    return (
      <div className="max-w-xl mx-auto p-6 text-center">
        <p className="text-gray-600 mb-4">Meeting request not found or link may have expired.</p>
        <Link to="/" className="text-blue-600 hover:underline">
          Go to Member Portal
        </Link>
      </div>
    );
  }

  const myPending =
    request.myRsvpStatus === 'pending' ||
    (request.status === 0 && (!request.myRsvpStatus || request.myRsvpStatus === 'pending'));
  const showActions = !done && myPending;

  return (
    <div className="max-w-xl mx-auto p-6">
      <h1 className="text-xl font-bold text-gray-900 mb-4">Meeting Request</h1>
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
        <p>
          <span className="font-medium">From:</span> {request.requesterName}
          {request.requesterMemberName ? ` (${request.requesterMemberName})` : ''}
        </p>
        <p>
          <span className="font-medium">Time:</span>{' '}
          {formatInstantRange(request.startUtc, request.endUtc, request.timeZoneId)}
        </p>
        {request.message ? (
          <p>
            <span className="font-medium">Details:</span> {request.message}
          </p>
        ) : null}
        {request.participants && request.participants.length > 0 ? (
          <div className="pt-2 border-t border-gray-200">
            <p className="font-medium mb-2">Participants</p>
            <ul className="space-y-2">
              {request.participants.map((p) => (
                <li key={`${p.role}-${p.userId}`} className="flex items-center justify-between gap-2">
                  <span>
                    {p.name || `User ${p.userId}`}
                    {p.memberName ? (
                      <span className="text-gray-500 text-sm"> · {p.memberName}</span>
                    ) : null}
                  </span>
                  <span
                    className={`text-xs font-semibold px-2 py-0.5 rounded ${rsvpBadgeClass(
                      p.status,
                      p.role
                    )}`}
                  >
                    {rsvpLabel(p.status, p.role)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
      {done ? (
        <div className="mt-6 p-4 rounded-lg bg-green-50 border border-green-200">
          <p className="text-green-800 font-medium">
            {done === 'accept' ? 'Your RSVP was recorded as Accepted.' : 'Your RSVP was recorded as Declined.'}
          </p>
          <Link to="/" className="text-blue-600 hover:underline mt-2 inline-block">
            Go to Member Portal
          </Link>
        </div>
      ) : showActions ? (
        <div className="mt-6 flex gap-4">
          <button
            type="button"
            onClick={handleAccept}
            disabled={submitting}
            className="px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            {submitting ? 'Processing...' : 'Accept'}
          </button>
          <button
            type="button"
            onClick={handleDecline}
            disabled={submitting}
            className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50"
          >
            Decline
          </button>
        </div>
      ) : (
        <p className="mt-4 text-gray-600">
          {request.myRsvpStatus === 'accepted' && 'You have already accepted this meeting.'}
          {request.myRsvpStatus === 'declined' && 'You have already declined this meeting.'}
          {!request.myRsvpStatus && request.status === 1 && 'This meeting has already been booked.'}
          {request.status === 2 && 'This meeting request was declined.'}
          {request.status === 3 && 'This meeting request was cancelled.'}
        </p>
      )}
    </div>
  );
}
